import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  Dispatch,
  ReactNode,
} from 'react';
import {Project} from '../lib/api-client';
import {apiClient} from '../lib/api-client';
import {apiConfig} from '../lib/api-client';

type ProjectsState = {
  currentProject: Project | null;
  projects: Project[];
};

type ProjectsAction =
  | {type: 'SET_PROJECTS'; payload: Project[]}
  | {type: 'SET_CURRENT_PROJECT'; payload: Project | null}
  | {type: 'UPDATE_PROJECT'; payload: Partial<Project> & Pick<Project, 'id'>}
  | {type: 'REMOVE_PROJECT'; payload: string};

type ProjectsDataContextValue = {
  state: ProjectsState;
};

type ProjectsApiContextValue = {
  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project | null) => void;
  updateProject: (project: Project) => void;
  removeProject: (projectId: string) => void;
  refreshCurrentProject: (projectId: string) => Promise<void>;
};

const ProjectsContextData = createContext<ProjectsDataContextValue | undefined>(
  undefined
);
const ProjectsContextApi = createContext<ProjectsApiContextValue | undefined>(
  undefined
);

const initialState: ProjectsState = {
  currentProject: null,
  projects: [],
};

function projectsReducer(
  state: ProjectsState,
  action: ProjectsAction
): ProjectsState {
  switch (action.type) {
    case 'SET_PROJECTS':
      return {...state, projects: action.payload};

    case 'SET_CURRENT_PROJECT':
      return {...state, currentProject: action.payload};

    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload.id ? {...p, ...action.payload} : p
        ),
        currentProject:
          state.currentProject?.id === action.payload.id
            ? {...state.currentProject, ...action.payload}
            : state.currentProject,
      };

    case 'REMOVE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.payload),
        currentProject:
          state.currentProject?.id === action.payload
            ? null
            : state.currentProject,
      };

    default:
      return state;
  }
}

export default function ProjectsContextProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const [state, dispatch] = useReducer(projectsReducer, initialState);

  const value = useMemo(() => {
    return {state};
  }, [state]);

  const refreshCurrentProject = useCallback(async (projectId: string) => {
    try {
      // Lightweight summary (status/role only) — not the full project record.
      // Always merge (UPDATE_PROJECT), never replace: the summary payload
      // doesn't carry name/sections/email_template_config/etc., so a full
      // replace here would wipe out everything already in state.
      const response = await apiClient.get(
        apiConfig.endpoints.projects.summary(projectId)
      );
      const summary = (response.data as any)?.data || response.data;
      if (summary) {
        dispatch({type: 'UPDATE_PROJECT', payload: {id: projectId, ...summary}});
      }
    } catch (error) {
      console.error('Failed to refresh current project:', error);
    }
  }, []);

  const api = useMemo(
    () => ({
      setProjects: (projects: Project[]) =>
        dispatch({type: 'SET_PROJECTS', payload: projects}),
      setCurrentProject: (project: Project | null) =>
        dispatch({type: 'SET_CURRENT_PROJECT', payload: project}),
      updateProject: (project: Project) =>
        dispatch({type: 'UPDATE_PROJECT', payload: project}),
      removeProject: (projectId: string) =>
        dispatch({type: 'REMOVE_PROJECT', payload: projectId}),
      refreshCurrentProject,
    }),
    [refreshCurrentProject]
  );

  return (
    <ProjectsContextData.Provider value={value}>
      <ProjectsContextApi.Provider value={api}>
        {children}
      </ProjectsContextApi.Provider>
    </ProjectsContextData.Provider>
  );
}

export function useProjectsData() {
  const context = useContext(ProjectsContextData);
  if (!context) {
    throw new Error(
      'useProjectsData must be used within ProjectsContextProvider'
    );
  }
  return context;
}

export function useProjectsApi() {
  const context = useContext(ProjectsContextApi);
  if (!context) {
    throw new Error(
      'useProjectsApi must be used within ProjectsContextProvider'
    );
  }
  return context;
}
